import { useState } from "react";
import { Copy, Check, Mail, Link as LinkIcon, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

// Premium invitation success dialog. Confirms an email has been sent AND lets
// the admin copy the raw activation link as a fallback (great for support).
export function InviteLinkDialog({ open, onOpenChange, link, name, email }) {
  const [copied, setCopied] = useState(false);
  const fullUrl = link ? `${window.location.origin}${link}` : "";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      toast.success("Invitation link copied");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Couldn't copy link");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="glass-strong shadow-premium overflow-hidden rounded-3xl border-border/60 p-0 sm:max-w-lg"
        data-testid="invite-link-dialog"
      >
        {/* Header with subtle gradient wash */}
        <div className="relative border-b border-border/60 bg-gradient-to-br from-primary/8 via-transparent to-transparent px-6 pb-5 pt-6">
          <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/12 text-primary ring-1 ring-inset ring-primary/20 shadow-[0_6px_20px_-8px_hsl(var(--primary)/0.5)]">
            <Mail className="h-5 w-5" />
          </div>
          <DialogHeader className="text-left">
            <DialogTitle className="font-display text-xl font-semibold tracking-tight text-foreground">
              Invitation sent{name ? ` to ${name}` : ""}
            </DialogTitle>
            <DialogDescription className="mt-1 text-sm text-muted-foreground">
              We've emailed {email ? <span className="font-medium text-foreground">{email}</span> : "the invitee"} a
              secure link to set their password and activate their account. Links expire in 7 days.
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="space-y-4 px-6 py-5">
          {/* Delivery confirmation row */}
          <div className="flex items-center gap-3 rounded-xl border border-emerald-500/20 bg-emerald-500/8 px-3.5 py-2.5">
            <ShieldCheck className="h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
            <p className="text-xs text-emerald-700 dark:text-emerald-300">
              Email delivered via secure transactional service. If it doesn't arrive within a few minutes,
              share the direct link below.
            </p>
          </div>

          {/* Fallback link box — wraps to fit any screen, monospace for legibility */}
          <div>
            <div className="mb-1.5 flex items-center gap-1.5">
              <LinkIcon className="h-3 w-3 text-muted-foreground" />
              <p className="overline">Direct activation link</p>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-stretch">
              <div className="min-w-0 flex-1 rounded-lg border border-border/60 bg-muted/30 p-2.5">
                <code
                  className="block break-all font-mono text-[11px] leading-relaxed text-foreground"
                  data-testid="invite-link-value"
                >
                  {fullUrl}
                </code>
              </div>
              <Button
                size="default"
                variant={copied ? "default" : "outline"}
                onClick={copy}
                className="shrink-0 gap-1.5 sm:h-auto"
                data-testid="copy-invite-link"
              >
                {copied ? (
                  <>
                    <Check className="h-3.5 w-3.5" />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy className="h-3.5 w-3.5" />
                    Copy link
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>

        <DialogFooter className="border-t border-border/60 bg-muted/20 px-6 py-4">
          <Button onClick={() => onOpenChange(false)} data-testid="invite-dialog-done" className="w-full sm:w-auto">
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
